import { MdCheckCircle, MdCancel, MdOutlineError } from 'react-icons/md';
import ComplexTable from 'components/table';
import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import Button from 'components/button';
import { HiArrowRight } from 'react-icons/hi';
import UseApi from 'hooks/useApi';
import { IFilter } from 'types';
import { encodeFilters } from 'utils';
import { useState } from 'react';
import BookingForm from './bookingForm';

type RowObj = {
  _id: string;
  package: { _id: string; packageName: string };
  user: { _id: string; username: string; email: string };
  billing: { totalAmount: number; receipt?: string };
  state: 'pending' | 'successful' | 'unsuccessful';
  createdAt: string;
};

const columnHelper = createColumnHelper<RowObj>();
const columns = [
  columnHelper.accessor('user', {
    id: 'user',
    header: () => <p className="  ">USER</p>,
    cell: (info) => (
      <p className="  ">
        {info.getValue()?.username || info.getValue()?.email}
      </p>
    ),
  }),
  columnHelper.accessor('package.packageName', {
    id: 'package',
    header: () => <p className="  ">PACKAGE</p>,
    cell: (info) => <p className="  ">{info.getValue()}</p>,
  }),
  columnHelper.accessor('createdAt', {
    id: 'date',
    header: () => <p className="  ">BOOKING DATE</p>,
    cell: (info) => <p className="  ">{info.getValue()?.substring(0, 10)}</p>,
  }),
  columnHelper.accessor('billing.totalAmount', {
    id: 'amount',
    header: () => <p className="  ">AMOUNT (PKR)</p>,
    cell: (info) => <p className="  ">{info.getValue()}</p>,
  }),
  columnHelper.accessor('state', {
    id: 'state',
    header: () => <p className="  ">STATUS</p>,
    cell: (info) => (
      <div className="flex items-center">
        {info.getValue() === 'successful' ? (
          <MdCheckCircle className="me-1 text-green-500 dark:text-green-300" />
        ) : info.getValue() === 'unsuccessful' ? (
          <MdCancel className="me-1 text-red-500 dark:text-red-300" />
        ) : info.getValue() === 'pending' ? (
          <MdOutlineError className="me-1 text-amber-500 dark:text-amber-300" />
        ) : null}
        <p className="text-md font-medium capitalize">{info.getValue()}</p>
      </div>
    ),
  }),
  columnHelper.accessor('billing.receipt', {
    id: 'payment',
    header: () => <p className="  ">PAYMENT</p>,
    cell: (info) => {
      const hasReceipt = info.getValue();
      return (
        <div className="flex items-center">
          {hasReceipt ? (
            <>
              <MdCheckCircle className="me-1 text-green-500 dark:text-green-300" />
              <p className="text-md font-medium text-green-600">Paid</p>
            </>
          ) : (
            <>
              <MdOutlineError className="me-1 text-amber-500 dark:text-amber-300" />
              <p className="text-md font-medium text-amber-600">Pending</p>
            </>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('_id', {
    id: '_id',
    header: () => <p className="  ">DETAILS</p>,
    cell: (info) => (
      <Link key={1} to={info.getValue()}>
        <Button type="icon">
          <HiArrowRight />
        </Button>
      </Link>
    ),
  }),
];

const filters: IFilter[] = [
  { key: 'user', type: 'text', label: 'User' },
  { key: 'package', type: 'text', label: 'Package' },
  { key: 'createdAtFrom', type: 'date', label: 'From Date' },
  { key: 'createdAtTo', type: 'date', label: 'To Date' },
  {
    key: 'state',
    type: 'select',
    options: ['pending', 'successful', 'unsuccessful'],
    label: 'Status',
  },
  {
    key: 'showArchived',
    type: 'select',
    options: ['false', 'true'],
    label: 'Archived',
  },
];

const Bookings = () => {
  const { getApi } = UseApi();

  async function fetchData(
    options: any,
    filterState: { [key: string]: string },
  ) {
    const { pageIndex, pageSize } = options;
    const response = await getApi(
      `bookings?page=${pageIndex + 1}&limit=${pageSize}${encodeFilters(
        filterState,
      )}`,
    );
    const data = response.data.data.bookings;
    const pageCount = response.data.data.totalPages;
    return {
      rows: data,
      pageCount,
    };
  }
  const [showCreatePackage, setShowCreatePackage] = useState(false);

  const handleToggleCreatePackage = () => {
    setShowCreatePackage((prevState) => !prevState);
  };

  return (
    <div>
      <Button
        onClick={handleToggleCreatePackage}
        extraClasses="ml-auto mr-4 mb-4"
      >
        {showCreatePackage ? 'Back' : 'Add New Booking'}
      </Button>
      {showCreatePackage ? (
        <BookingForm type="Create" handleToggleCreatePackage={handleToggleCreatePackage} />
      ) : (
        <ComplexTable
          columns={columns}
          fetchData={fetchData}
          filters={filters}
        />
      )}
    </div>
  );
};
export default Bookings;
